import {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate, useLocation} from 'react-router';
import {observer} from 'mobx-react-lite';
import PageLayoutTemplate from '@ui/templates/page-template/page-layout';
import {
  DelimitingWrapper,
  OuterWrapper,
  Wrapper,
  ButtonWrapper,
  SubmissionActionButton,
  ItemLoaderWrapper,
  FormGrid,
  FormContainer,
  GridItem,
  ChecklistRemoveButton,
  SubmissionBlock,
  SubmissionBlockContent,
  SubmissionBlockHeader,
  SubmissionBlockSubtitle,
  SubmissionBlockTitle,
  SubmissionTypeTag,
} from './my_checkins-style';
import {Button} from '@ui/atoms/button';
import {VerticalSpacer} from '@ui/atoms/spacer';
import {MoreAboutWorkLife} from './components/work-life';
import {MultiField} from '@ui/molecules/field/multifield/multifield';
import {useYouCheckinsHook} from '@pages/dashboard/you/you-components/you-checkins/you-checkins.hooks';
import {Controller} from 'react-hook-form';
import {useStoreContext} from '@store/store-context';
import {ItemLoader} from '@ui/organisms/item-loader';
import {MoodBox} from './components/mood-box';
import {checkinName} from '@utils/checkin-name';
import {capitalize} from '@utils';
import {CancelIcon} from '@ui/atoms/icons';
import {usePulseEmoji} from './hooks/usePulseEmoji';
import {CheckinHistory} from './components/checkin-history';
import {CompanyCheckinsRequestImpl} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.request';
import {CompanyCheckinsController} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.controller';
import {
  ICurrentCheckinsInfoResponseData,
  ISpecificCheckinsWithResponseData,
} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {useQuery} from 'react-query';
import {CheckinMetaCard} from './components/checkin-meta-card';
import {
  CheckinFormValues,
  formatSentLabel,
  getCheckinProgress,
  getCreatorName,
  getDueMeta,
} from './checkin-submit.utils';
import {
  getVisibleCheckinParams,
  isVisibleCheckinParam,
} from '../checklist-availability';
import {resolveParticipantCheckinRoute} from './hooks/useParticipantCheckinMetadata';
import {isGoalsReflectionQuestion} from '../create-checkin-cycle/components/goals-content';
import {useCheckinGoals} from './components/checkin-goals';
import {addGoalReflectionSnapshot} from './goal-reflection-snapshot';
import {activateNotification} from '@ui/molecules/notification/activate-notification';
import {useParticipantCheckinDeadlines} from './hooks/useParticipantCheckinDeadlines';
import {mergeCheckinDeadline} from '../shared/checkin-deadline.utils';

export const MyCheckinFormPage = observer(() => {
  const navigate = useNavigate();

  const location = useLocation();

  const routeSelection = useMemo(
    () => resolveParticipantCheckinRoute(location.search, location.state),
    [location.search, location.state],
  );

  const {
    handleSubmit,
    control,
    watch,
    isSubmitting,
    submitForm,
    debounceUpdatePulse,
    debounceUpdatePriorities,
    updatePulse,
    debounceUpdateQuestions,
    updateQuestions,
    handleFormValueChange,
    setIsReadOnly,
    isReadOnly,
    currentCheckin,
    cycleData,
    isSuperseded,
    latestCycleCheckin,
    isLoadingCurrentCheckin,
    isLoadingCycleData,
  } = useYouCheckinsHook();

  const checkinsRequest = useMemo(() => new CompanyCheckinsRequestImpl(), []);
  const checkinsController = useMemo(
    () => new CompanyCheckinsController(checkinsRequest),
    [checkinsRequest],
  );

  const {
    checkinsStore: {setActiveWorkspaceCheckinId},
    authStore: {auth},
    usersStore: {users},
  } = useStoreContext();
  const isAdmin = auth.user.role?.toLowerCase() === 'admin';
  const canViewHistory = auth.user.isReviewer || isAdmin;

  useEffect(() => {
    setActiveWorkspaceCheckinId(routeSelection.checkinId || null);
  }, [routeSelection.checkinId, setActiveWorkspaceCheckinId]);

  const [showHistory, setShowHistory] = useState(false);
  const [expandedSubmissions, setExpandedSubmissions] = useState<Set<string>>(
    new Set(),
  );
  const [submissionDetails] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!canViewHistory && showHistory) {
      setShowHistory(false);
    }
  }, [canViewHistory, showHistory]);

  const {emojiMap} = usePulseEmoji();

  // Type guard for currentCheckin (using the same logic as in the hook)
  const isValidCheckin = (
    checkin: any,
  ): checkin is
    | ICurrentCheckinsInfoResponseData
    | ISpecificCheckinsWithResponseData => {
    return checkin && typeof checkin === 'object' && checkin !== null;
  };
  const deadlineRows = useParticipantCheckinDeadlines(
    auth.user.id,
    auth.user.workspace?.id,
    isValidCheckin(currentCheckin) &&
      !currentCheckin.isSubmitted &&
      !currentCheckin.isExcused &&
      !isSuperseded,
  );

  // Get current cycle ID for filtering submission history
  const currentCycleId = useMemo(() => {
    if (cycleData && cycleData.id) {
      return cycleData.id;
    }

    if (isValidCheckin(currentCheckin) && 'cycle' in currentCheckin) {
      return currentCheckin.cycle as string;
    }

    return null;
  }, [cycleData, currentCheckin]);

  // Check if there are past submissions for this cycle to enable/disable history button
  const {data: pastSubmissions} = useQuery(
    ['user-checkins-list-check', auth.user.id, currentCycleId],
    async () => {
      if (!currentCycleId) return [];

      try {
        const response = await checkinsController.getListOfCheckins({
          user: auth.user.id,
          isSubmitted: true,
          paginate: false,
        });

        if (response && response.data && Array.isArray(response.data)) {
          // Filter by current cycle ID and exclude current checkin
          return response.data.filter((submission: any) => {
            const currentCheckinId = isValidCheckin(currentCheckin)
              ? currentCheckin.id
              : null;
            return (
              submission.cycle === currentCycleId &&
              submission.id !== currentCheckinId
            );
          });
        }
        return [];
      } catch (error) {
        return [];
      }
    },
    {
      enabled: canViewHistory && !!currentCycleId && !!auth.user.id,
      refetchOnWindowFocus: false,
    },
  );

  const hasPastSubmissions = canViewHistory && !!pastSubmissions?.length;

  const isExcusedCheckin =
    isValidCheckin(currentCheckin) && currentCheckin.isExcused === true;

  const canSubmit = isValidCheckin(currentCheckin)
    ? !currentCheckin.isReviewed && !isSuperseded && !isExcusedCheckin
    : false;

  const cycleTitle =
    (cycleData && cycleData.cycleName) || `${capitalize(checkinName())}`;

  const senderName = getCreatorName(cycleData?.createdBy, users);
  const dueMeta =
    isValidCheckin(currentCheckin) &&
    !currentCheckin.isSubmitted &&
    !isExcusedCheckin &&
    !isSuperseded
      ? getDueMeta(
          mergeCheckinDeadline(
            currentCheckin,
            currentCheckin.id ? deadlineRows.get(currentCheckin.id) : undefined,
          ),
        )
      : {isOverdue: false};
  const sentLabel = isValidCheckin(currentCheckin)
    ? formatSentLabel(currentCheckin)
    : 'Sent date unavailable';

  const formValues = watch() as CheckinFormValues;
  const hasGoalsReflection = Boolean(
    formValues?.questions?.questions?.some((question) =>
      isGoalsReflectionQuestion(question.question),
    ),
  );
  const goalsState = useCheckinGoals(
    auth.user.id,
    hasGoalsReflection && !isReadOnly,
  );
  const progressValues = useMemo<CheckinFormValues>(() => {
    const cycleQuestions = cycleData?.content?.questions || [];
    return {
      ...formValues,
      questions: {
        ...formValues?.questions,
        questions: (formValues?.questions?.questions || []).map((question) => {
          const configured = cycleQuestions.find(
            (cycleQuestion) =>
              cycleQuestion.questionId === question.questionId ||
              cycleQuestion.question === question.question,
          );
          return {
            ...question,
            required: configured?.required ?? question.required,
          };
        }),
      },
    };
  }, [cycleData?.content?.questions, formValues]);
  const submissionProgress = getCheckinProgress(
    progressValues,
    isValidCheckin(currentCheckin) ? currentCheckin : null,
  );

  // Get ordered content blocks for rendering based on cycle content
  const orderedContentBlocks = useMemo(() => {
    if (
      !cycleData ||
      !cycleData.content ||
      !isValidCheckin(currentCheckin) ||
      !currentCheckin.params
    ) {
      // Fallback to default order if no cycle data
      return isValidCheckin(currentCheckin) && currentCheckin.params
        ? ['pulse', 'questions', 'priorities'].filter(
            (param) =>
              currentCheckin.params?.includes(param) &&
              isVisibleCheckinParam(param),
          )
        : [];
    }

    // Use cycle content order - check what's available in the cycle content
    const availableBlocks = [];

    if (cycleData.content.pulse && currentCheckin.params.includes('pulse')) {
      availableBlocks.push('pulse');
    }
    if (
      cycleData.content.questions &&
      currentCheckin.params.includes('questions')
    ) {
      availableBlocks.push('questions');
    }
    if (
      isVisibleCheckinParam('priorities') &&
      cycleData.content.priorities &&
      currentCheckin.params.includes('priorities')
    ) {
      availableBlocks.push('priorities');
    }

    return availableBlocks;
  }, [cycleData, currentCheckin]);

  // Render helper for each content block type
  const renderContentBlock = useCallback(
    (blockType: string) => {
      if (
        !isValidCheckin(currentCheckin) ||
        !currentCheckin.params?.includes(blockType) ||
        !isVisibleCheckinParam(blockType)
      ) {
        return null;
      }

      switch (blockType) {
        case 'pulse':
          return (
            <div key="pulse">
              <Controller
                control={control}
                name="pulse"
                render={({value}) => (
                  <MoodBox
                    value={value}
                    isSubmitted={
                      isValidCheckin(currentCheckin)
                        ? !!currentCheckin.responses?.pulse?.mood
                        : false
                    }
                    isRequired={
                      isValidCheckin(currentCheckin)
                        ? currentCheckin.responses?.pulse?.required ?? true
                        : true
                    }
                    prompt={cycleData?.content?.pulse?.[0]?.name}
                    debounceUpdatePulse={debounceUpdatePulse}
                    updatePulse={updatePulse}
                    isReadOnly={isReadOnly}
                    handleFormValueChange={handleFormValueChange}
                    isLoadingCycleData={isLoadingCycleData}
                  />
                )}
              />
              <VerticalSpacer size="24px" />
            </div>
          );

        case 'questions':
          return (
            <div key="questions">
              <Controller
                control={control}
                name="questions"
                render={({value}) => {
                  // Merge workspace checkin questions with cycle data questions to get answerType
                  const enhancedValue = value
                    ? {
                        ...value,
                        questions:
                          value.questions?.map((workspaceQuestion: any) => {
                            // Find matching question in cycle data
                            const cycleQuestion =
                              cycleData &&
                              cycleData.content?.questions?.find(
                                (cq: any) =>
                                  cq.question === workspaceQuestion.question,
                              );

                            return {
                              ...workspaceQuestion,
                              answerType: cycleQuestion
                                ? cycleQuestion.answerType || 'text'
                                : 'text',
                              required: cycleQuestion
                                ? cycleQuestion.required ??
                                  workspaceQuestion.optional === false
                                : workspaceQuestion.optional === false,
                            };
                          }) || [],
                      }
                    : value;

                  return (
                    <MoreAboutWorkLife
                      value={enhancedValue}
                      isReadOnly={isReadOnly}
                      debounceUpdateQuestions={debounceUpdateQuestions}
                      handleFormValueChange={handleFormValueChange}
                      isLoadingCycleData={isLoadingCycleData}
                      goalsState={goalsState}
                    />
                  );
                }}
              />
              <VerticalSpacer size="24px" />
            </div>
          );

        case 'priorities':
          return (
            <div key="priorities">
              <Controller
                control={control}
                name="priorities"
                render={({value}) => {
                  const checklistItems = (value?.currentCheckin as Array<{
                    text?: string;
                    completed?: boolean;
                  }>) || [{text: '', completed: false}];

                  const isChecklistReadOnly =
                    isReadOnly ||
                    (isValidCheckin(currentCheckin)
                      ? currentCheckin.isReviewed
                      : false);

                  const checklistConfig = cycleData?.content?.priorities?.[0];
                  return (
                    <SubmissionBlock $completed={false}>
                      <SubmissionBlockHeader>
                        <SubmissionTypeTag $type="checklist">
                          Checklist
                        </SubmissionTypeTag>
                        <SubmissionBlockTitle>
                          {checklistConfig?.title ||
                            value?.title ||
                            'Checklist'}
                        </SubmissionBlockTitle>
                        <SubmissionBlockSubtitle>
                          Locked items are added by {senderName} and can be
                          completed in this check-in or the next.
                        </SubmissionBlockSubtitle>
                      </SubmissionBlockHeader>
                      <SubmissionBlockContent>
                        <div className="flex flex-col gap-4">
                          {isLoadingCycleData ? (
                            <ItemLoader />
                          ) : (
                            <MultiField
                              label=""
                              id="checkins-checklist"
                              value={checklistItems.map((item, index) => ({
                                text: item?.text || '',
                                key: `${value?.id || 'checklist'}-${index}`,
                                completed: item?.completed,
                              }))}
                              updateValue={(updated?: Array<any>) => {
                                const mapped =
                                  updated?.map((item, index) => ({
                                    text: item?.text || '',
                                    completed:
                                      item?.completed ??
                                      checklistItems[index]?.completed ??
                                      false,
                                  })) || [];
                                const updatedValue = {
                                  ...value,
                                  currentCheckin: mapped,
                                };
                                handleFormValueChange(
                                  updatedValue,
                                  'priorities',
                                );
                                debounceUpdatePriorities({
                                  priorities: updatedValue,
                                  prioritiesId: value?.id,
                                });
                              }}
                              emitLatestValueOnChange
                              fontWeight={400}
                              colorString="blackTwo"
                              checkedIndexes={[0]}
                              showCheckbox
                              userCanReorder={false}
                              size="compact"
                              placeholder="Add list item"
                              addFieldPlaceholder="Add to list"
                              readonly={isChecklistReadOnly}
                              dropdownComponent={(
                                onHandleRemove: () => void,
                              ) => (
                                <ChecklistRemoveButton
                                  type="button"
                                  onClick={onHandleRemove}>
                                  <CancelIcon
                                    style={{width: 24, height: 24}}
                                    color="#5F5F8C"
                                  />
                                </ChecklistRemoveButton>
                              )}
                            />
                          )}
                        </div>
                      </SubmissionBlockContent>
                    </SubmissionBlock>
                  );
                }}
              />
              <VerticalSpacer size="24px" />
            </div>
          );

        default:
          return null;
      }
    },
    [
      currentCheckin,
      control,
      debounceUpdatePulse,
      updatePulse,
      isReadOnly,
      handleFormValueChange,
      debounceUpdateQuestions,
      goalsState,
      debounceUpdatePriorities,
      cycleData,
      isLoadingCycleData,
      senderName,
    ],
  );

  const onSubmitCheckin = useCallback(
    async (data: any) => {
      if (isExcusedCheckin || !submissionProgress.canSubmit) return;
      const preparedData = addGoalReflectionSnapshot(data, goalsState.goals);

      if (preparedData !== data && preparedData.questions) {
        const questionsSaved = await updateQuestions({
          questions: preparedData.questions as any,
          questionId: String((preparedData.questions as any).id || ''),
        });

        if (!questionsSaved) {
          activateNotification({
            title: 'Unable to submit check-in',
            content: 'Your goal snapshot could not be saved. Please try again.',
            kind: 'error',
          });
          return;
        }

        handleFormValueChange(preparedData.questions, 'questions');
      }

      const submitted = await submitForm(preparedData);
      if (submitted) {
        navigate('/checkins/my-checkins', {replace: true});
      }
    },
    [
      goalsState.goals,
      handleFormValueChange,
      isExcusedCheckin,
      navigate,
      submissionProgress.canSubmit,
      submitForm,
      updateQuestions,
    ],
  );

  const isLoading = isLoadingCurrentCheckin;

  const FormContent = !isLoading ? (
    <OuterWrapper onSubmit={handleSubmit(onSubmitCheckin)}>
      <DelimitingWrapper>
        <FormContainer showHistory={showHistory}>
          <FormGrid showHistory={showHistory}>
            <GridItem dividerSide={showHistory ? 'right' : undefined}>
              <Wrapper>
                <div className={showHistory ? 'pl-6' : ''}>
                  <CheckinMetaCard
                    senderName={senderName}
                    sentLabel={sentLabel}
                    dueLabel={dueMeta.dueLabel}
                    dueDateLabel={dueMeta.dueDateLabel}
                    isOverdue={dueMeta.isOverdue}
                    answered={submissionProgress.answered}
                    total={submissionProgress.total}
                    percentage={submissionProgress.percentage}
                    showHistory={showHistory}
                    onToggleHistory={() => setShowHistory((prev) => !prev)}
                    hasPastSubmissions={hasPastSubmissions}
                    canViewHistory={canViewHistory}
                  />

                  {isExcusedCheckin ? (
                    <div className="mb-6 rounded-[10px] border border-borderLight bg-white px-6 py-8 text-center">
                      <div className="text-base font-semibold leading-6 text-blackTwo">
                        This check-in is excused
                      </div>
                      <div className="mt-2 text-sm leading-[22px] text-textBody">
                        You don't need to submit this check-in.
                      </div>
                    </div>
                  ) : isSuperseded ? (
                    <div className="mb-6 rounded-[10px] border border-borderLight bg-white px-6 py-8 text-center">
                      <div className="text-base font-semibold leading-6 text-blackTwo">
                        This check-in is no longer available
                      </div>
                      <div className="mt-2 text-sm leading-[22px] text-textBody">
                        A newer check-in from this cycle has been sent. Older
                        check-ins can no longer be filled.
                      </div>
                      {latestCycleCheckin?.id && (
                        <Button
                          type="button"
                          kind="secondary"
                          style={{marginTop: 20}}
                          onClick={() => {
                            setActiveWorkspaceCheckinId(latestCycleCheckin.id!);
                            navigate(
                              `/checkins/my-checkin?checkinId=${encodeURIComponent(
                                latestCycleCheckin.id!,
                              )}&mode=${
                                latestCycleCheckin.isSubmitted ? 'view' : 'edit'
                              }`,
                            );
                          }}>
                          Go to latest check-in
                        </Button>
                      )}
                    </div>
                  ) : Object.keys(currentCheckin || {}).length > 0 ? (
                    <>
                      {orderedContentBlocks.map((blockType) =>
                        renderContentBlock(blockType),
                      )}
                      {getVisibleCheckinParams(
                        isValidCheckin(currentCheckin)
                          ? currentCheckin.params
                          : [],
                      ).length === 0 && (
                        <div className="mb-6 rounded-[10px] border border-borderLight bg-white px-6 py-8 text-center text-sm leading-[22px] text-textBody">
                          There are no response fields in this check-in.
                        </div>
                      )}
                    </>
                  ) : null}
                  {canSubmit &&
                    (!isReadOnly ||
                      (isValidCheckin(currentCheckin) &&
                        currentCheckin.isSubmitted)) && (
                      <ButtonWrapper
                        as="section"
                        aria-label="submission-actions">
                        {isReadOnly && (
                          <Button
                            width="full"
                            type="button"
                            kind="secondary"
                            onClick={() => setIsReadOnly(false)}>
                            Edit {checkinName()}s
                          </Button>
                        )}

                        {!isReadOnly && (
                          <SubmissionActionButton
                            width="full"
                            type="submit"
                            data-form-action={true}
                            isLoading={isSubmitting}
                            aria-busy={isSubmitting}
                            disabled={
                              isSubmitting ||
                              !submissionProgress.canSubmit ||
                              (hasGoalsReflection &&
                                !isReadOnly &&
                                (goalsState.isLoading || goalsState.isError))
                            }
                            className="w-full">
                            {isValidCheckin(currentCheckin) &&
                            currentCheckin.isSubmitted
                              ? 'Save changes'
                              : 'Submit check-in'}
                          </SubmissionActionButton>
                        )}
                      </ButtonWrapper>
                    )}
                </div>
              </Wrapper>
            </GridItem>

            {canViewHistory && showHistory && (
              <CheckinHistory
                emojiMap={emojiMap}
                onDismiss={() => setShowHistory(false)}
                expandedSubmissions={expandedSubmissions}
                submissionDetails={submissionDetails}
                checkinsController={checkinsController}
                currentCycleId={currentCycleId}
                userId={auth.user.id}
                onToggleExpand={(submissionId: string) => {
                  const newExpanded = new Set(expandedSubmissions);
                  if (newExpanded.has(submissionId)) {
                    newExpanded.delete(submissionId);
                  } else {
                    newExpanded.add(submissionId);
                  }
                  setExpandedSubmissions(newExpanded);
                }}
              />
            )}
          </FormGrid>
        </FormContainer>
      </DelimitingWrapper>
    </OuterWrapper>
  ) : (
    <ItemLoaderWrapper className="bg-backgroundLight">
      <ItemLoader />
    </ItemLoaderWrapper>
  );

  return (
    <PageLayoutTemplate
      title={cycleTitle}
      onClick={() => navigate('/checkins/my-checkins')}
      bodyStyle={{padding: 0, margin: 0, backgroundColor: '#fafafa'}}>
      <div>{FormContent}</div>
    </PageLayoutTemplate>
  );
});

export default MyCheckinFormPage;
